import { Controller } from "@hotwired/stimulus"


/**
* Stimulus controller for real-time form filtering with Turbo Frame updates.
* Handles debounced input, multi-select values, URL param management, and Select2 integration
*
* Usage:
* <form data-controller="turbo-form-search"
*       data-turbo-form-search-frame-id-value="results"
*       data-turbo-form-search-target-url-value="/search"
*       data-action="input->turbo-form-search#filter change->turbo-form-search#filter">
*   <input name="q" type="text">
*   <select name="category" multiple data-select2>...</select>
* </form>
*/

// TODO:Rename to realtime_filter_controller.js
export default class extends Controller {
  static values = {
    frameId: { type: String, default: "content" },
    targetUrl: { type: String, default: "" },
    defaultSort: { type: String, default: "account_name" }
  }

  connect() {
    this.params = new URLSearchParams(window.location.search)
    this.params.delete('page')
    this.timeout = null
    this.restoreFocus()
    this.initializeSelect2()

    this.element.addEventListener('search:pause', this.handlePause.bind(this))
    this.element.addEventListener('search:unpause', this.handleUnpause.bind(this))
  }

  disconnect() {
    this.element.removeEventListener('search:pause', this.handlePause.bind(this))
    this.element.removeEventListener('search:unpause', this.handleUnpause.bind(this))
  }

  handlePause() {
    this.paused = true
  }

  handleUnpause() {
    this.paused = false
    this.rebuildParamsFromForm()
    this.visitWithTurboFrame()
  }

  rebuildParamsFromForm() {
    // Clear existing params
    this.params = new URLSearchParams()
    this.params.delete('page')
    
    // Read all form inputs and rebuild params
    const formData = new FormData(this.element)
    
    for (let [key, value] of formData.entries()) {
      if (value !== '' && value !== null) {
        this.params.append(key, value)
      }
    }
  }

  filter(event) {
    event.preventDefault()

    if (this.paused) return

    clearTimeout(this.timeout)
    
    if (event.type === "change" || event.type === "select2:select" || event.type === "select2:unselect") {
      this.handleFilter(event)
    } else {
      this.timeout = setTimeout(() => {this.handleFilter(event)}, 500)
    }
  }

  handleFilter(event) {
    const name = event.target.name
    const value = event.target.value
    
    if(name == 'date_range') {
      this.handleDate(value)
    } else if (event.target.selectedOptions && event.target.multiple) {
      this.handleMultiselect(name, value, event.target.selectedOptions)
    } else if (event.target.type === 'checkbox') {
      this.handleCheckbox(name, event.target.checked)
    } else {
      this.handleSingleValue(name, value)
    }
    
    this.visitWithTurboFrame()
  }

  visitWithTurboFrame() {
    const frameId = this.frameIdValue
    const frame = document.getElementById(frameId)
    
    if (!frame) {
      console.warn(`Turbo frame with id "${frameId}" not found`)
      return
    }

    // Dispatch event to notify that filters changed
    // frame.dispatchEvent(new CustomEvent('filters:changed', {
    //   bubbles: true,
    //  detail: { params: this.params.toString() }
    // }))

    const baseUrl = this.targetUrlValue || window.location.pathname
    const url = `${baseUrl}?${this.params.toString()}`

    frame.src = url

    const newUrl = `${window.location.pathname}?${this.params.toString()}`
    window.history.replaceState({}, '', newUrl)
  }

  handleDate(value) {
    if(value === "") {
      this.params.delete('start_date')
      this.params.delete('end_date')
      return
    }
    
    const dates = value.split(' - ')
    this.params.set('start_date', dates[0])
    this.params.set('end_date', dates[1])
  }

  handleMultiselect(name, value, options) {
    this.params.delete(name)
    const selectedOptions = Array.from(options)
    selectedOptions.forEach(option => {
      this.params.append(name, option.value)
    })
  }

  handleSingleValue(name, value) {
    if(value != "") {
      this.params.set(name, value)

      // If direction is set but sort is not, use the default sort key
      if (name === 'direction' && !this.params.get('sort')) {
        this.params.set('sort', this.defaultSortValue)
      }

      // Opposite scenario: just default to 'asc'
      if (name === 'sort' && !this.params.get('direction')) {
        this.params.set('direction', 'asc');
      }
    } else {
      this.params.delete(name)
    }
  }

  handleCheckbox(name, checked) {
    if(checked) {
      this.params.set(name, true)
    } else {
      this.params.delete(name)
    }
  }

  restoreFocus() {
    const searchInput = this.element.querySelector('input[name="q"]')
    if(!searchInput) return
    
    const valueLength = searchInput.value.length
    searchInput.focus()
    
    if (searchInput.setSelectionRange) {
      searchInput.setSelectionRange(valueLength, valueLength)
    } else if (searchInput.createTextRange) {
      const range = searchInput.createTextRange()
      range.collapse(true)
      range.moveEnd('character', valueLength)
      range.moveStart('character', valueLength)
      range.select()
    }
  }

  initializeSelect2() {
    const select2Elements = this.element.querySelectorAll('select[data-select2]')
    
    select2Elements.forEach(element => {
      const $element = $(element)
      const selectedValues = this.params.getAll(element.name)
      
      if (selectedValues.length) {
        $element.val(selectedValues)
      }
      
      $element.on('select2:select select2:unselect', (e) => {
        this.filter(e)
      })
    })
  }
}
